import { useCallback, useEffect } from "react";

import { Icon } from "@ospweb/sdk-ui";
import { formatAddress } from "utils";
import log from "logbeacon";
import { EdgenModalCreateAble } from "../EdgenModal";
import { AccountItem } from "./AccountItem";
import { DisConnectModalCallable } from "./DisConnectModal";
import { WalletLoadingModalCallable } from "./WalletLoadingModal";
import { BindTypeEnum } from "@/src/features/api/openApi";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Setting/ConnectWallet";
import { useBindWallet } from "@/src/hooks";
import { useSingleBind } from "@/src/hooks/useSingleBind";

const I18n = I18nUtil.createScoped(Translations);

interface ConnectWalletProps {
  wallets: any[];
}

export const ConnectWallet = ({ wallets }: ConnectWalletProps) => {
  const { onReconnect, isLoading, isConnecting } = useBindWallet({});
  const { unBind } = useSingleBind({
    type: BindTypeEnum.WALLET,
  });
  const showLoadingModal = useCallback(async () => {
    try {
      await WalletLoadingModalCallable({
        title: I18n.t("Continue in the wallet…"),
        description: I18n.t("Chill a bit. We're making your wallet happier"),
      });
    } catch (error) {
      log.error("[文案]显示弹窗失败:", error);
    }
  }, []); // 稳定的回调，避免重复创建

  useEffect(() => {
    log.debug("[文案]isLoading 状态变化:", isLoading);
    if (isLoading) {
      // 添加防抖，避免频繁调用
      const timer = setTimeout(() => {
        showLoadingModal();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      //当 isLoading 为 false 时，主动关闭弹窗
      try {
        EdgenModalCreateAble.end(false);
      } catch (error) {
        log.error("[文案]关闭弹窗失败:", error);
      }
    }
  }, [isLoading, showLoadingModal]);

  const handleUnlink = async (platform_id: string) => {
    await DisConnectModalCallable({
      title: I18n.t("Unlink wallet"),
      description: I18n.t("Are you sure you want to unlink this wallet now?"),
      onConfirm: async () => {
        await unBind({ address: platform_id });
      },
    });
  };
  return (
    <div className="flex flex-col gap-4 w-full p-6 border border-background_white_overlay10 rounded-xl">
      <div className="flex justify-between items-center w-full gap-2">
        <div className="flex flex-col gap-2">
          <div className="text-content_primary headline_regular_s">
            {I18n.t("Link Your Wallet")}
          </div>
          <div className="text-content_secondary body_s">
            {I18n.t(
              "Tell Edgen where to send your future rewards. Aura farmers gotta get paid.",
            )}
          </div>
        </div>
        {wallets?.length === 0 && (
          <button
            aria-label="Connect Wallet"
            data-openreplay-label={`Connect Wallet Button | ${I18n.t("Connect")}`}
            id="btn-connect-wallet"
            className="sm_button button primary_button text-content_inverse_primary button_m"
            onClick={onReconnect}
            disabled={isConnecting || isLoading}
          >
            {I18n.t("Connect")}
          </button>
        )}
        {wallets?.length > 0 && wallets?.length < 6 && (
          <button
            aria-label="Connect Wallet"
            data-openreplay-label="Connect Wallet Button"
            id="btn-connect-wallet-2"
            className="sm_button button secondary_button text-content_primary button_m gap-1"
            onClick={() => {
              onReconnect();
            }}
            disabled={isConnecting || isLoading}
          >
            <Icon name="plus" size={20} />
            {I18n.t("Add")}
          </button>
        )}
      </div>
      {wallets?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {wallets?.map((wallet) => (
            <AccountItem
              key={wallet?.platform_id}
              className="w-full"
              username={formatAddress(wallet?.platform_id, 4)}
              address={wallet?.platform_id}
              icon="wallet"
              onUnlink={() => handleUnlink(wallet?.platform_id)}
              isCopy
            />
          ))}
        </div>
      )}
    </div>
  );
};
